/**
 * Public page — no Microsoft login required.
 * Route: /werkgroepen-en-leden?token=…
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { DOMINICUS_LOGO_URL, WERKGROEP_INTAKE_LINK_VALIDITY_DAYS } from "@domaddress/shared";

const API_URL = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_URL ?? "http://localhost:3000";

async function publicGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { headers: { "Content-Type": "application/json" } });
  const json = await res.json() as T;
  if (!res.ok) throw new Error((json as { error?: string })?.error ?? `HTTP ${res.status}`);
  return json;
}

async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json() as T;
  if (!res.ok) throw new Error((json as { error?: string })?.error ?? `HTTP ${res.status}`);
  return json;
}

// ── types ─────────────────────────────────────────────────────────────────────

type SavedLid = {
  sort_order: number;
  first_name: string;
  tussenvoegsel: string | null;
  last_name: string;
  email: string | null;
  phone: string | null;
};

type ValidateOk = {
  valid: true;
  intake_id: number;
  status: string;
  group_name: string;
  chair_prefill: {
    first_name: string | null;
    tussenvoegsel: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  };
  revision_feedback?: string | null;
  saved_leden: SavedLid[];
  suggested_leden_count: number;
};

type ValidateFail = { valid: false; expired?: boolean; status: string; group_name: string };
type ValidateResult = ValidateOk | ValidateFail;

type LidRow = {
  first_name: string;
  tussenvoegsel: string;
  last_name: string;
  email: string;
  phone: string;
};

function emptyLid(): LidRow {
  return { first_name: "", tussenvoegsel: "", last_name: "", email: "", phone: "" };
}

// ── Page-scoped responsive CSS ────────────────────────────────────────────────

const PAGE_CSS = `
.intake-page {
  min-height: 100vh;
  background: var(--surface-100, #faf8fc);
  padding: 0 0 3rem;
  font-family: "Metropolis", system-ui, "Segoe UI", sans-serif;
}
.intake-header {
  background: linear-gradient(145deg, #0f0a2e 0%, #1a1450 40%, #3a0d2e 100%);
  border-bottom: 3px solid #ee2361;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.intake-body {
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem 1rem;
}
.intake-revision-banner {
  background: #fff7ed;
  border: 1px solid #fdba74;
  border-radius: var(--radius-md, 10px);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.88rem;
  color: #9a3412;
}
  border: 1px solid #c7d2fe;
  border-radius: var(--radius-md, 10px);
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.88rem;
  color: #3730a3;
}
.intake-save-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}
.intake-save-badge {
  font-size: 0.8rem;
  padding: 3px 10px;
  border-radius: 99px;
  font-weight: 500;
  transition: opacity 0.3s;
}
.intake-save-badge--saving {
  background: var(--surface-200, #f3f0f7);
  color: var(--text-600, #5c526d);
}
.intake-save-badge--saved {
  background: #d1fae5;
  color: #065f46;
}
.intake-save-badge--error {
  background: var(--danger-light, #fee2e2);
  color: var(--danger, #dc2626);
}
.intake-section {
  background: white;
  border: 1px solid var(--surface-300, #e4dfe8);
  border-radius: var(--radius-lg, 18px);
  padding: 1.1rem 1.25rem;
  box-shadow: var(--shadow-sm);
  margin-bottom: 0.85rem;
}
.intake-section-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-700, #453a58);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.85rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--surface-200, #f3f0f7);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.intake-name-grid {
  display: grid;
  grid-template-columns: 1fr minmax(5.25rem, 6.75rem) 1fr;
  gap: 10px;
  margin-bottom: 10px;
}
.intake-name-grid .form-group {
  min-width: 0;
}
.intake-name-grid .form-group label {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.intake-two-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
@media (max-width: 520px) {
  .intake-name-grid,
  .intake-two-grid {
    grid-template-columns: 1fr;
  }
  .intake-name-grid {
    grid-template-rows: auto;
  }
}
.intake-add-btn {
  width: 100%;
  border: 1.5px dashed var(--surface-400, #c9c2d4);
  border-radius: var(--radius-md, 10px);
  background: none;
  padding: 0.6rem;
  color: var(--text-500, #736a82);
  font-size: 0.88rem;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  margin-top: 0.25rem;
}
.intake-add-btn:hover {
  border-color: var(--accent, #ee2361);
  color: var(--accent, #ee2361);
  background: color-mix(in srgb, var(--accent, #ee2361) 5%, transparent);
}
.intake-remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-400, #8a8394);
  font-size: 0.95rem;
  padding: 2px 6px;
  border-radius: 4px;
  line-height: 1;
}
.intake-remove-btn:hover {
  color: var(--danger, #dc2626);
  background: var(--danger-light, #fee2e2);
}
.intake-error {
  background: var(--danger-light, #fee2e2);
  border: 1px solid #fca5a5;
  border-radius: var(--radius-sm, 6px);
  padding: 0.6rem 0.9rem;
  color: var(--danger, #dc2626);
  font-size: 0.875rem;
  margin-top: 0.75rem;
}
.intake-success {
  background: #d1fae5;
  border: 1px solid #6ee7b7;
  border-radius: var(--radius-md, 10px);
  padding: 1rem 1.25rem;
  color: #065f46;
  font-size: 0.95rem;
}
.intake-state-card {
  max-width: 480px;
  margin: 2rem auto;
  background: white;
  border: 1px solid var(--surface-300, #e4dfe8);
  border-radius: var(--radius-lg, 18px);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  text-align: center;
}
.intake-state-card h2 {
  font-size: 1.15rem;
  color: var(--text-900, #1a1230);
  margin-bottom: 0.6rem;
}
.intake-state-card p {
  color: var(--text-600, #5c526d);
  font-size: 0.9rem;
  line-height: 1.55;
}
`;

// ── Sub-components ────────────────────────────────────────────────────────────

function FormGroup({
  label,
  title,
  required,
  children,
}: {
  label: string;
  /** Uitgebreide uitleg bij hover (bijv. afgekorte labeltekst). */
  title?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="form-group">
      <label title={title}>
        {label}
        {required && <span style={{ color: "var(--accent, #ee2361)", marginLeft: 2 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function FieldInput({
  value,
  onChange,
  placeholder,
  type = "text",
  maxLength = 200,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  required?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      required={required}
      style={{
        padding: "9px 12px",
        border: "1px solid var(--surface-300, #e4dfe8)",
        borderRadius: "var(--radius-md, 10px)",
        fontSize: "14px",
        color: "var(--text-900, #1a1230)",
        background: "white",
        outline: "none",
        transition: "border 160ms, box-shadow 160ms",
        width: "100%",
        boxSizing: "border-box",
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "var(--navy-600, #3f32a8)";
        e.target.style.boxShadow = "0 0 0 3px rgba(44,33,133,0.18)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "var(--surface-300, #e4dfe8)";
        e.target.style.boxShadow = "none";
      }}
    />
  );
}

function LidCard({
  index,
  lid,
  onChange,
  onRemove,
  canRemove,
}: {
  index: number;
  lid: LidRow;
  onChange: (f: keyof LidRow, v: string) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  return (
    <div className="intake-section" style={{ marginBottom: "0.65rem" }}>
      <div className="intake-section-title">
        <span>Lid {index + 1}</span>
        {canRemove && (
          <button type="button" className="intake-remove-btn" onClick={onRemove} title="Verwijderen">
            ✕ verwijder
          </button>
        )}
      </div>
      <div className="intake-name-grid">
        <FormGroup label="Voornaam" required>
          <FieldInput value={lid.first_name} onChange={(v) => onChange("first_name", v)} placeholder="Voornaam" required />
        </FormGroup>
        <FormGroup label="Tussenv." title="Tussenvoegsel (tussennaam, bijv. van, de, ter)">
          <FieldInput value={lid.tussenvoegsel} onChange={(v) => onChange("tussenvoegsel", v)} placeholder="van" maxLength={50} />
        </FormGroup>
        <FormGroup label="Achternaam" required>
          <FieldInput value={lid.last_name} onChange={(v) => onChange("last_name", v)} placeholder="Achternaam" required />
        </FormGroup>
      </div>
      <div className="intake-two-grid">
        <FormGroup label="E-mailadres">
          <FieldInput value={lid.email} onChange={(v) => onChange("email", v)} placeholder="naam@voorbeeld.nl" type="email" maxLength={254} />
        </FormGroup>
        <FormGroup label="Telefoon">
          <FieldInput value={lid.phone} onChange={(v) => onChange("phone", v)} placeholder="06-12345678" type="tel" maxLength={40} />
        </FormGroup>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function WerkgroepIntakePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [loadState, setLoadState] = useState<"loading" | "invalid" | "expired" | "done" | "already_handled">("loading");
  const [validateData, setValidateData] = useState<ValidateOk | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const [chairFirst, setChairFirst] = useState("");
  const [chairTussen, setChairTussen] = useState("");
  const [chairLast, setChairLast] = useState("");
  const [chairEmail, setChairEmail] = useState("");
  const [chairPhone, setChairPhone] = useState("");
  const [leden, setLeden] = useState<LidRow[]>([emptyLid()]);

  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [submitError, setSubmitError] = useState("");

  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);

  // ── Load & validate token ─────────────────────────────────────────────────

  const validate = useCallback(async () => {
    if (!token) { setLoadState("invalid"); return; }
    try {
      const res = await publicGet<{ data: ValidateResult }>(
        `/api/werkgroep-intakes/public/validate?token=${encodeURIComponent(token)}`
      );
      const d = res.data;
      if (!d.valid) {
        if (d.expired) setLoadState("expired");
        else if (d.status === "approved" || d.status === "rejected") setLoadState("already_handled");
        else setLoadState("invalid");
        return;
      }
      setValidateData(d);

      // Restore saved draft leden, or fall back to prefill count
      if (d.saved_leden && d.saved_leden.length > 0) {
        setLeden(d.saved_leden.map((l) => ({
          first_name: l.first_name ?? "",
          tussenvoegsel: l.tussenvoegsel ?? "",
          last_name: l.last_name ?? "",
          email: l.email ?? "",
          phone: l.phone ?? "",
        })));
      } else {
        const count = Math.max(1, d.suggested_leden_count ?? 1);
        setLeden(Array.from({ length: count }, emptyLid));
      }

      // Prefill chair fields from saved data (or original contact card data)
      setChairFirst(d.chair_prefill.first_name ?? "");
      setChairTussen(d.chair_prefill.tussenvoegsel ?? "");
      setChairLast(d.chair_prefill.last_name ?? "");
      setChairEmail(d.chair_prefill.email ?? "");
      setChairPhone(d.chair_prefill.phone ?? "");

      setLoadState("done");
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : "Onbekende fout");
      setLoadState("invalid");
    }
  }, [token]);

  useEffect(() => { void validate(); }, [validate]);

  // ── Auto-save ─────────────────────────────────────────────────────────────

  async function saveDraft() {
    if (!token || loadState !== "done" || submitState === "success") return;
    setSaveState("saving");
    try {
      await publicPost("/api/werkgroep-intakes/public/save-draft", {
        token,
        chair_first_name: chairFirst.trim() || undefined,
        chair_tussenvoegsel: chairTussen.trim() || undefined,
        chair_last_name: chairLast.trim() || undefined,
        chair_email: chairEmail.trim() || undefined,
        chair_phone: chairPhone.trim() || undefined,
        leden: leden.map((l) => ({
          first_name: l.first_name.trim(),
          tussenvoegsel: l.tussenvoegsel.trim() || undefined,
          last_name: l.last_name.trim(),
          email: l.email.trim() || undefined,
          phone: l.phone.trim() || undefined,
        })),
      });
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 3000);
    } catch {
      setSaveState("error");
    }
  }

  // Debounced auto-save: skip on the very first render after prefill
  useEffect(() => {
    if (loadState !== "done") return;
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => { void saveDraft(); }, 1500);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chairFirst, chairTussen, chairLast, chairEmail, chairPhone, leden, loadState]);

  // ── Leden helpers ─────────────────────────────────────────────────────────

  function updateLid(i: number, field: keyof LidRow, value: string) {
    setLeden((prev) => prev.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));
  }
  function addLid() { setLeden((prev) => [...prev, emptyLid()]); }
  function removeLid(i: number) { setLeden((prev) => prev.filter((_, idx) => idx !== i)); }

  // ── Submit ────────────────────────────────────────────────────────────────

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    for (const lid of leden) {
      if (!lid.first_name.trim() || !lid.last_name.trim()) {
        setSubmitError("Vul voor elk lid ten minste voornaam en achternaam in.");
        return;
      }
    }
    setSubmitState("submitting");
    setSubmitError("");
    try {
      await publicPost("/api/werkgroep-intakes/public/submit", {
        token,
        chair_first_name: chairFirst.trim(),
        chair_tussenvoegsel: chairTussen.trim() || undefined,
        chair_last_name: chairLast.trim(),
        chair_email: chairEmail.trim() || undefined,
        chair_phone: chairPhone.trim() || undefined,
        leden: leden.map((l) => ({
          first_name: l.first_name.trim(),
          tussenvoegsel: l.tussenvoegsel.trim() || undefined,
          last_name: l.last_name.trim(),
          email: l.email.trim() || undefined,
          phone: l.phone.trim() || undefined,
        })),
      });
      setSubmitState("success");
    } catch (err: unknown) {
      setSubmitState("error");
      setSubmitError(err instanceof Error ? err.message : "Er is een fout opgetreden.");
    }
  }

  // ── Render helpers ────────────────────────────────────────────────────────

  function Header() {
    return (
      <header className="intake-header">
        <img src={DOMINICUS_LOGO_URL} alt="Dominicus Amsterdam" height={44} style={{ height: 44, width: "auto", flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", letterSpacing: "0.02em" }}>Adresboek</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.82)", marginTop: 2 }}>Dominicus Amsterdam</div>
        </div>
      </header>
    );
  }

  if (loadState === "loading") {
    return (
      <div className="intake-page">
        <style>{PAGE_CSS}</style>
        <Header />
        <div className="intake-body">
          <div style={{ display: "flex", justifyContent: "center", paddingTop: "3rem" }}>
            <div className="loading-spinner" />
          </div>
        </div>
      </div>
    );
  }

  if (loadState !== "done") {
    const titles: Record<string, string> = {
      expired: "Link verlopen",
      already_handled: "Al afgehandeld",
      invalid: "Ongeldige link",
    };
    const msgs: Record<string, string> = {
      expired: `Deze uitnodigingslink is verlopen (geldig voor ${WERKGROEP_INTAKE_LINK_VALIDITY_DAYS} dagen). Vraag de secretaris om een nieuwe link.`,
      already_handled: "Deze ledenlijst is al beoordeeld door de secretaris. Er zijn geen verdere acties nodig.",
      invalid: errorMsg || "Deze link is ongeldig of al gebruikt. Neem contact op met de secretaris.",
    };
    return (
      <div className="intake-page">
        <style>{PAGE_CSS}</style>
        <Header />
        <div className="intake-body">
          <div className="intake-state-card">
            <h2>{titles[loadState] ?? "Fout"}</h2>
            <p>{msgs[loadState] ?? "Onbekende fout."}</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitState === "success") {
    return (
      <div className="intake-page">
        <style>{PAGE_CSS}</style>
        <Header />
        <div className="intake-body">
          <div className="intake-state-card">
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>✓</div>
            <h2>Bedankt!</h2>
            <p>
              De ledenlijst van <strong>{validateData?.group_name}</strong> is ontvangen en wordt zo snel
              mogelijk door de secretaris beoordeeld. U kunt dit venster sluiten.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────

  return (
    <div className="intake-page">
      <style>{PAGE_CSS}</style>
      <Header />
      <div className="intake-body">

        {/* Title bar + auto-save indicator */}
        <div className="intake-save-bar">
          <div>
            <h1 style={{ fontSize: "1.15rem", fontWeight: 700, color: "var(--text-900, #1a1230)", margin: 0 }}>
              Ledenlijst — {validateData?.group_name}
            </h1>
          </div>
          {saveState === "saving" && (
            <span className="intake-save-badge intake-save-badge--saving">Opslaan…</span>
          )}
          {saveState === "saved" && (
            <span className="intake-save-badge intake-save-badge--saved">✓ Opgeslagen</span>
          )}
          {saveState === "error" && (
            <span className="intake-save-badge intake-save-badge--error">Opslaan mislukt</span>
          )}
        </div>

        {/* Come-back-later banner */}
        <div className="intake-banner">
          <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>💾</span>
          <span>
            <strong>Uw voortgang wordt automatisch bewaard.</strong> U kunt deze pagina sluiten en later
            terugkeren via dezelfde link in de e-mail — uw gegevens staan er dan nog in.
          </span>
        </div>

        {validateData?.revision_feedback?.trim() && (
          <div className="intake-revision-banner">
            <strong>Bericht van de secretaris</strong>
            <p style={{ margin: "0.45rem 0 0", whiteSpace: "pre-wrap" }}>{validateData?.revision_feedback}</p>
            <p style={{ margin: "0.5rem 0 0", fontSize: "0.82rem", opacity: 0.95 }}>
              Pas hieronder de gegevens aan en dien de lijst opnieuw in als u klaar bent.
            </p>
          </div>
        )}

        <form onSubmit={(e) => { void handleSubmit(e); }}>

          {/* Voorzitter section */}
          <div className="intake-section">
            <div className="intake-section-title">Uw gegevens (voorzitter)</div>
            <div className="intake-name-grid">
              <FormGroup label="Voornaam" required>
                <FieldInput value={chairFirst} onChange={setChairFirst} placeholder="Voornaam" required />
              </FormGroup>
              <FormGroup label="Tussenv." title="Tussenvoegsel (tussennaam, bijv. van, de, ter)">
                <FieldInput value={chairTussen} onChange={setChairTussen} placeholder="van" maxLength={50} />
              </FormGroup>
              <FormGroup label="Achternaam" required>
                <FieldInput value={chairLast} onChange={setChairLast} placeholder="Achternaam" required />
              </FormGroup>
            </div>
            <div className="intake-two-grid">
              <FormGroup label="E-mailadres">
                <FieldInput value={chairEmail} onChange={setChairEmail} placeholder="voorzitter@voorbeeld.nl" type="email" maxLength={254} />
              </FormGroup>
              <FormGroup label="Telefoon">
                <FieldInput value={chairPhone} onChange={setChairPhone} placeholder="06-12345678" type="tel" maxLength={40} />
              </FormGroup>
            </div>
          </div>

          {/* Leden section */}
          <div style={{ marginTop: "1.1rem" }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "0.6rem" }}>
              <h2 style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--text-800, #2d2245)", margin: 0 }}>
                Leden <span style={{ color: "var(--text-400, #8a8394)", fontWeight: 400 }}>({leden.length})</span>
              </h2>
              <span style={{ fontSize: "0.78rem", color: "var(--text-500, #736a82)" }}>
                Voornaam en achternaam zijn verplicht per lid.
              </span>
            </div>

            {leden.map((lid, i) => (
              <LidCard
                key={i}
                index={i}
                lid={lid}
                onChange={(f, v) => updateLid(i, f, v)}
                onRemove={() => removeLid(i)}
                canRemove={leden.length > 1}
              />
            ))}

            <button type="button" className="intake-add-btn" onClick={addLid}>
              + Lid toevoegen
            </button>
          </div>

          {submitError && <p className="intake-error">{submitError}</p>}

          <div style={{ marginTop: "1.5rem", display: "flex", justifyContent: "flex-end", gap: "0.6rem", flexWrap: "wrap" }}>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitState === "submitting"}
              style={{ fontSize: "0.95rem", padding: "10px 24px" }}
            >
              {submitState === "submitting" ? "Verzenden…" : "Ledenlijst indienen"}
            </button>
          </div>

          <p style={{ marginTop: "0.75rem", fontSize: "0.78rem", color: "var(--text-400, #8a8394)", textAlign: "right" }}>
            Na het indienen ontvangt de secretaris een melding ter beoordeling.
          </p>
        </form>
      </div>
    </div>
  );
}
